import { useState } from 'react';
import { Button, CircularLoader, TextButton, Typography } from 'seagull';
import { routes } from "../../../../constants";
import { ErrorMessage, FormContainer, FormFields, VerticalFormFields } from "../../components/FormComponents";
import { useEventReporter, useIoContext, useStoreActions } from "../../../../contexts";
import { handlePromise } from "../../../../infra/utils";
import { useUtmsParams } from "../../../../hooks/useUtmsParams";
import OTPVerificationField from "../../../../components/OTPVerificationField/OTPVerificationField";
import styles from "./OTPForm.module.scss";
import { FormSeparatorHorizontal } from "../../components/FormSeparator";
import { notices } from "./OTPField.utils";
import { LoginFlow } from "../LoginScreen.types";
import { useOnMount } from "../../../../hooks/useOnMount";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export default function OTPForm(_ref) {
  let {
    email,
    flow,
    onBack,
    shouldRequestOTP
  } = _ref;
  const {
    reportFormSubmission,
    reportToHeap
  } = useEventReporter();
  const {
    locationReload
  } = useIoContext();
  const {
    api,
    logBI
  } = useStoreActions();
  const getUtms = useUtmsParams();
  const [otpSessionStatus, setOtpSessionStatus] = useState();
  const {
    changeLocation
  } = useIoContext();
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const notice = notices[otpSessionStatus !== null && otpSessionStatus !== void 0 ? otpSessionStatus : ''];
  const inlineError = (notice === null || notice === void 0 ? void 0 : notice.isError) && (notice === null || notice === void 0 ? void 0 : notice.displaysInline) && (notice === null || notice === void 0 ? void 0 : notice.text);
  const floatingError = (notice === null || notice === void 0 ? void 0 : notice.isError) && !(notice !== null && notice !== void 0 && notice.displaysInline) && (notice === null || notice === void 0 ? void 0 : notice.text);
  const floatingMessage = !(notice !== null && notice !== void 0 && notice.isError) && (notice === null || notice === void 0 ? void 0 : notice.text);
  useOnMount(() => {
    async function requestOTPOnMount() {
      if (shouldRequestOTP) {
        const result = flow === LoginFlow.EmailVerification ? await handlePromise(api.auth.requestNewOTPForSignupFlow(email)) : await handlePromise(api.auth.requestNewOTP(email));
        if (!result.success) {
          setOtpSessionStatus(getErrorCause(result.error));
        }
      }
    }
    requestOTPOnMount();
  });
  async function validateOTP(otp) {
    setIsLoading(true);
    const result = flow === LoginFlow.EmailVerification ? await handlePromise(api.auth.validateOTPForSignupFlow(email, otp, getUtms())) : await handlePromise(api.auth.validateOTP(email, otp, getUtms()));
    if (!result.success) {
      setOtpSessionStatus(getErrorCause(result.error));
      setHasError(true);
      setIsLoading(false);
    } else {
      reportFormSubmission('general-logged-in-successfully', null);
      reportToHeap('Login Screen', 'click', 'signed-in-with-six-digits-code', {
        email
      });
      logBI(16001, {
        email
      });
      if (flow === LoginFlow.EmailVerification) {
        changeLocation(routes.home);
      } else {
        locationReload();
      }
    }
  }
  async function requestNewOTP() {
    logBI(16000, {
      email
    });
    reportToHeap('Login Screen', 'click', 'clicked-send-me-six-digits-code', {
      email
    });
    const result = flow === LoginFlow.EmailVerification ? await handlePromise(api.auth.requestNewOTPForSignupFlow(email)) : await handlePromise(api.auth.requestNewOTP(email));
    if (!result.success) {
      setOtpSessionStatus(getErrorCause(result.error));
    } else {
      setOtpSessionStatus('otpRequestOK');
    }
  }
  function getErrorCause(error) {
    if (error.response) {
      if (error.response.status === 400) {
        return 'wrongCode';
      } else if (error.response.status === 429) {
        return 'tooManyRequests';
      }
    }
    return 'unknown';
  }
  return /*#__PURE__*/_jsxs(FormContainer, {
    onSubmit: e => e.preventDefault(),
    "data-testid": "otp-form",
    className: styles.otpForm,
    children: [/*#__PURE__*/_jsx(Typography, {
      "data-testid": "otp-form-title",
      variant: "h5",
      weight: "normal",
      className: styles.title,
      children: flow === LoginFlow.EmailVerification ? 'Verify your email' : 'Check your email'
    }), /*#__PURE__*/_jsx(FormFields, {
      className: styles.formFields,
      children: /*#__PURE__*/_jsxs(VerticalFormFields, {
        className: styles.verticalFormFields,
        children: [/*#__PURE__*/_jsx("div", {
          className: styles.subtitle,
          children: /*#__PURE__*/_jsxs(Typography, {
            children: ["Enter the 6-digit code sent to", ' ', /*#__PURE__*/_jsx(Typography, {
              as: "span",
              weight: "bold",
              children: email
            })]
          })
        }), /*#__PURE__*/_jsx(OTPVerificationField, {
          onChange: validateOTP,
          hasError: hasError,
          onInputChange: () => {
            if (notice !== null && notice !== void 0 && notice.isError) {
              setOtpSessionStatus(undefined);
            }
          }
        }), isLoading && /*#__PURE__*/_jsx("div", {
          className: styles.loadingContainer,
          "data-testid": "loading-container",
          children: /*#__PURE__*/_jsx(CircularLoader, {
            size: 24,
            centeredInBox: true
          })
        }), inlineError && /*#__PURE__*/_jsx(ErrorMessage, {
          "data-testid": "error-message",
          children: inlineError
        }), floatingMessage && /*#__PURE__*/_jsx(Typography, {
          variant: "paragraph2",
          "data-testid": "info-message",
          className: styles.floatingMessage,
          children: floatingMessage
        }), floatingError && /*#__PURE__*/_jsx(ErrorMessage, {
          "data-testid": "error-message",
          children: floatingError
        })]
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.footer,
      children: [/*#__PURE__*/_jsxs("div", {
        className: styles.newCodeContent,
        children: [/*#__PURE__*/_jsx(FormSeparatorHorizontal, {
          className: styles.separator,
          children: "Couldn't find it in your inbox or spam folder?"
        }), /*#__PURE__*/_jsx(Button, {
          skin: "primaryOutline",
          "data-testid": "get-new-code-button",
          size: "medium",
          underline: "none",
          hoverSkin: "primaryOutline",
          className: styles.getNewCodeButton,
          onClick: async e => {
            e.preventDefault();
            requestNewOTP();
          },
          children: "get a new code"
        })]
      }), /*#__PURE__*/_jsx(TextButton, {
        underline: "none",
        skin: "secondary",
        onClick: onBack,
        "data-testid": "back-link",
        className: styles.backButton,
        children: "Back"
      })]
    })]
  });
}